import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const PLAN_TYPES = [
  'Simple Will Package',
  'Revocable Trust Package',
  'Powers of Attorney Only',
  'Not Sure / Needs Attorney Guidance'
];

const CreateMatter = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedClientId = searchParams.get('clientId');

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    client_id: '',
    plan_type: '',
    advisor_notes: ''
  });

  useEffect(() => {
    const fetchClients = async () => {
      if (!profile?.advisor_firm_id) {
        setLoading(false);
        return;
      }
      try {
        const { data, error: clientError } = await supabase
          .from('clients')
          .select('id, first_name, last_name, email, state_of_residence')
          .eq('advisor_firm_id', profile.advisor_firm_id)
          .order('last_name', { ascending: true });

        if (clientError) throw clientError;
        setClients(data || []);
        
        // Handle pre-selection
        if (preSelectedClientId && data?.some(c => c.id === preSelectedClientId)) {
          setFormData(prev => ({ ...prev, client_id: preSelectedClientId }));
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
        setError('Failed to load client list.');
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [profile, preSelectedClientId]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id || !formData.plan_type) return;

    setSubmitting(true);
    setError(null);

    try {
      const selectedClient = clients.find(c => c.id === formData.client_id);
      const { data: inheritedAssignment, error: assignmentError } = await supabase
        .from('matters')
        .select('law_firm_id, assigned_attorney_id')
        .eq('client_id', formData.client_id)
        .eq('advisor_firm_id', profile.advisor_firm_id)
        .not('law_firm_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (assignmentError) throw assignmentError;
      
      const { data: matter, error: matterError } = await supabase
        .from('matters')
        .insert([{
          client_id: formData.client_id,
          advisor_firm_id: profile.advisor_firm_id,
          advisor_user_id: profile.id,
          law_firm_id: inheritedAssignment?.law_firm_id || null,
          assigned_attorney_id: inheritedAssignment?.assigned_attorney_id || null,
          matter_type: 'estate_plan',
          plan_type: formData.plan_type,
          status: 'matter_created',
          legal_fee_status: 'not_started',
          complexity_level: 'unknown',
          client_state: selectedClient.state_of_residence,
          advisor_notes: formData.advisor_notes
        }])
        .select()
        .single();

      if (matterError) throw matterError;

      setSuccess(true);
      setTimeout(() => navigate(`/matters/${matter.id}`), 1500);
    } catch (err) {
      console.error('Matter creation error:', err);
      setError(err.message || "Failed to create plan.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="py-20 text-center text-slate-500">Loading form...</div>;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center mb-8">
        <button onClick={() => navigate(-1)} className="mr-4 p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
          <SafeIcon icon={FiIcons.FiArrowLeft} className="h-6 w-6" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Create New Plan</h1>
          <p className="text-sm text-slate-500">Initiate an estate plan for an existing client.</p>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
        {success ? (
          <div className="p-12 text-center">
            <div className="bg-green-100 p-3 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiIcons.FiCheck} className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Plan Created!</h2>
            <p className="text-slate-500 mt-2">Opening plan details...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">{error}</div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Select Client *</label>
              <select 
                name="client_id" 
                required 
                value={formData.client_id} 
                onChange={handleChange} 
                className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border outline-none bg-white"
                disabled={!!preSelectedClientId}
              >
                <option value="">-- Choose a client --</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.first_name} {client.last_name} ({client.email})
                  </option>
                ))}
              </select>
              {preSelectedClientId && (
                <p className="mt-1 text-xs text-slate-400 italic">Pre-selected from client list</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Plan Type *</label>
              <select name="plan_type" required value={formData.plan_type} onChange={handleChange} className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border outline-none bg-white">
                <option value="">-- Select package --</option>
                {PLAN_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Advisor Notes</label>
              <textarea name="advisor_notes" rows={4} value={formData.advisor_notes} onChange={handleChange} className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border outline-none" placeholder="Provide context for the law firm..." />
            </div>

            <div className="pt-4 border-t border-slate-100 flex justify-end items-center space-x-4">
              <button type="button" onClick={() => navigate('/dashboard')} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800">Cancel</button>
              <button type="submit" disabled={submitting} className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-900 disabled:opacity-50 transition-colors">
                {submitting ? 'Creating...' : 'Create Plan'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateMatter;
